import { useMemo } from 'react'

import type { AiFinding } from '../../lib/api/aiFindings.ts'
import { buildRadar, statusLabel, verdictPresentation } from '../../lib/ai/semantics.ts'
import { formatAxisTime, formatClockShort, formatInt } from '../../lib/format.ts'

/**
 * Máquinas × janelas de 15 min, do que está carregado. Uma linha acesa em
 * janelas seguidas é campanha, não ruído — é isso que a lista sozinha esconde.
 */
export function FindingRadar({
  findings,
  selectedId,
  onPick,
}: {
  findings: readonly AiFinding[]
  selectedId: string | null
  onPick: (finding: AiFinding) => void
}) {
  const radar = useMemo(() => buildRadar(findings), [findings])
  if (radar.rows.length === 0) return null

  const ticks = radar.slots.filter((_, i) => i % 8 === 0)

  return (
    <div className="overflow-x-auto border-b border-line-subtle px-3 py-2">
      <div className="grid min-w-[28rem] grid-cols-[9rem_1fr] gap-x-2 gap-y-0.5">
        {radar.rows.map((row) => (
          <div key={row.endpointId} className="contents">
            <span className="truncate font-mono text-micro leading-4 text-fg-secondary" title={row.host}>
              {row.host}
            </span>
            <div className="flex gap-px">
              {row.cells.map((finding, i) => (
                <Cell
                  key={radar.slots[i]}
                  finding={finding}
                  selected={finding !== null && finding.id === selectedId}
                  onPick={onPick}
                />
              ))}
            </div>
          </div>
        ))}
        <span className="text-micro text-fg-muted">
          {radar.hiddenHosts > 0 ? `+${formatInt(radar.hiddenHosts)} máquinas` : ''}
        </span>
        <div className="flex gap-px">
          {radar.slots.map((slot) => (
            <span key={slot} className="max-w-6 min-w-2.5 flex-1 overflow-visible whitespace-nowrap text-micro text-fg-muted tnum">
              {ticks.includes(slot) ? formatAxisTime(new Date(slot)) : ''}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

function Cell({
  finding,
  selected,
  onPick,
}: {
  finding: AiFinding | null
  selected: boolean
  onPick: (finding: AiFinding) => void
}) {
  if (finding === null) {
    return <span aria-hidden className="h-4 max-w-6 min-w-2.5 flex-1 bg-inset" />
  }

  const verdict = verdictPresentation(finding.verdict)
  const look =
    verdict !== null
      ? verdict.fill
      : finding.status === 'analysis_failed'
        ? 'border border-crit'
        : 'border border-dashed border-line-strong'
  const decided = finding.status === 'dismissed' ? 'opacity-35' : ''
  const ring = selected ? 'outline outline-1 outline-offset-1 outline-fg' : ''
  const label = `${finding.host} · ${formatClockShort(finding.window_start)}–${formatClockShort(finding.window_end)} UTC · ${
    verdict?.label ?? statusLabel(finding.status)
  }`

  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={() => onPick(finding)}
      className={`h-4 max-w-6 min-w-2.5 flex-1 ${look} ${decided} ${ring} hover:outline hover:outline-1 hover:outline-fg-muted`}
    />
  )
}

/** Legenda do radar: a cor nunca é o único canal, então cada uma tem nome. */
export function RadarLegend() {
  const items: Array<{ cls: string; label: string }> = [
    { cls: 'bg-crit', label: 'Ataque' },
    { cls: 'bg-high', label: 'Suspeito' },
    { cls: 'bg-line-strong', label: 'Inconclusivo' },
    { cls: 'bg-ok', label: 'Benigno' },
    { cls: 'border border-dashed border-line-strong', label: 'Só triagem' },
    { cls: 'border border-crit', label: 'Falha' },
  ]
  return (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-micro text-fg-muted">
      {items.map((item) => (
        <span key={item.label} className="inline-flex items-center gap-1">
          <span aria-hidden className={`inline-block h-3 w-2 ${item.cls}`} />
          {item.label}
        </span>
      ))}
    </span>
  )
}
